import Trajectory from './Trajectory'
import Victor from 'victor'
import PolygonLookup from 'polygon-lookup'
import { TrajectoryProblem } from '../types'
import paper from "paper";

interface Polygon {
    x: number
    y: number
}

export default class SegmentTree {
    private lookup: PolygonLookup

    constructor(trajectory: Trajectory, maxWidth: number) {
        const features = []
        const points = trajectory.getAllPoints()
        for (let i = 1; i < points.length; i++) {
            const polygon = []
            const previous = points[i - 1]
            polygon.push(trajectory.getPoint(previous.md, maxWidth))
            polygon.push(trajectory.getPoint(points[i].md, maxWidth))
            polygon.push(trajectory.getPoint(points[i].md, -maxWidth))
            polygon.push(trajectory.getPoint(previous.md, -maxWidth))

            // calculate cross point.
            const f1 = this.normalEquation(trajectory, previous.md, maxWidth)
            const f2 = this.normalEquation(trajectory, points[i].md, maxWidth)
            const intersectionPoint = intersection(f1, f2)

            const start = trajectory.getPoint(previous.md, 0)
            const end = trajectory.getPoint(points[i].md, 0)
            const segmentEquation = equation(start, end)
            const segmentLength = new paper.Point(end.x, end.y).subtract(start).length
            const len = new paper.Point(intersectionPoint.x, intersectionPoint.y).subtract(start).length

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    properties: {
                        intersection: new Victor(intersectionPoint.x, intersectionPoint.y),
                        len: previous.point.clone().subtract(points[i].point).length(),
                        mdStart: previous.md,
                        mdEnd: points[i].md,
                        previous,
                        distance: len,
                        startPoint: new paper.Point(start.x, start.y),
                        segmentEquation,
                        segmentLength
                    },
                    coordinates: [polygon.map(e => [e.x, e.y])]
                }
            })
        }

        this.lookup = new PolygonLookup({
            type: 'FeatureCollection',
            features
        })
    }

    getMD([x, y]: [number, number]): number | undefined {
        const found = this.lookup.search(x, y)
        if (found) {
            const properties = found.geometry.properties

            if (properties.distance == Infinity) {
                // Using approximation by the beginning of the segment when it is unable to project md
                return properties.mdStart
            }

            const mouseToIntersectionEquation = equation(new Victor(x, y), properties.intersection)
            const segmentIntersection = intersection(properties.segmentEquation, mouseToIntersectionEquation)
            const length = properties.startPoint.subtract(segmentIntersection).length
            const delta = length / properties.segmentLength * (properties.mdEnd - properties.mdStart)
            const md = properties.mdStart + delta

            return md < properties.mdEnd ? md : properties.mdEnd
        }
    }

    private normalEquation(trajectory, md: number, maxWidth: number) {
        const point = trajectory.getPoint(md, 0)
        const normal = point.vector.multiply(maxWidth)
        return equation(point, point.subtract(normal))
    }
}

export function intersection(f1: { s: number; b: number }, f2: { s: number; b: number }): { x: number; y: number } {
    const intersectionX = (f2.b - f1.b) / (f1.s - f2.s)

    const intersectionY = f1.s * intersectionX + f1.b

    return {
        x: intersectionX,
        y: intersectionY
    }
}

export function equation(victor1: Victor, victor2: Victor): { s: number; b: number } {
    const s = (victor2.y - victor1.y) / (victor2.x - victor1.x)
    const b = victor1.y - s * victor1.x

    return {
        s,
        b
    }
}
